<script setup lang="ts">
import { computed } from 'vue'
import { type LocationAsRelativeRaw } from 'vue-router'
import i18n from '@/scripts/app-configs/i18n-config'
import AllMemberKits from '@/components/AllMemberKits.vue'
import PatientGoals from '@/components/drawers/MemberDrawer/MemberDrawerContent/MemberDrawerContentPatientGoals.vue'
import PatientReminders from '@/components/drawers/MemberDrawer/MemberDrawerContent/MemberDrawerContentPatientReminders.vue'
import WidgetLink from '@/components/drawers/MemberDrawer/MemberDrawerContent/MemberDrawerContentWidgetLink.vue'
import PatientContacts from '@/components/drawers/MemberDrawer/MemberDrawerContent/MemberDrawerContentPatientContacts.vue'
import PatientPrescription from '@/components/drawers/MemberDrawer/MemberDrawerContent/MemberDrawerContentPatientPrescription.vue'
import MemberDrawerContentHeader from '@/components/drawers/MemberDrawer/MemberDrawerContent/MemberDrawerContentHeader.vue'
import TemporaryTransferPill from '@/components/micro/Pills/TemporaryTransferPill.vue'
import { PILL_STATUS_TYPES } from '@/components/micro/Pills/BasePill/base-pill.types'

import { PATIENT_UNIT as UNIT } from '@/scripts/configs/patient-units'
import { LeaveFeedEventDestination, FeedEventSource } from '@/utils/segment/segment.types'
import type {
  Profile,
  CareCoordinator,
  Reminder,
  Goal,
  ClinicalInformation,
  MemberDrawerProtocolInfo,
  Contact,
  MemberDrawerProtocol,
  Kit,
  Eligibility,
  Context,
} from '@/components/drawers/MemberDrawer/types/member-drawer.types'

interface MemberDrawerContentProps {
  profileInfo: Profile
  therapistId: number
  careCoordinators: CareCoordinator[]
  reminders: Reminder[]
  goals: Goal[]
  clinicalInfo?: Partial<ClinicalInformation>
  protocol?: MemberDrawerProtocolInfo
  appVersion: string
  age?: number | string
  currentState: string | null
  clinicalPortalBaseUrl?: string
  contacts?: Contact[]
  bloomProtocol: MemberDrawerProtocol
  kitHistory: Kit[]
  eligibility?: Eligibility
}

const props = withDefaults(defineProps<MemberDrawerContentProps>(), {
  clinicalInfo: () => ({}),
  contacts: () => [],
  eligibility: () => ({}),
})

const emit = defineEmits<{
  (e: 'close-side-bar'): void,
  (e: 'copy-to-clipboard', payload: { context: Context, content?: string }): void,
  (e: 'open-ui', payload: { context: Context }): void,
  (e: 'change-attention-level', payload: string): void,
  (e: 'send-to-user', payload: { context: Context }): void,
  (e: 'leave-feed', payload: { source: FeedEventSource, destination: LeaveFeedEventDestination }): void,
  (e: 'start-call', payload: { source: FeedEventSource }): void,
}>()

const userIsLead = computed(() => {
  const authUserID = props.therapistId
  const leadID = props.profileInfo.lead_id
  const ownerID = props.profileInfo.owner?.id

  return authUserID === leadID && authUserID !== ownerID
})

const kitText = computed(() => {
  const { clinicalInfo, profileInfo } = props

  if (clinicalInfo?.no_pod) {
    return i18n.t('SIDEBAR.KIT_BLOOM_NO-POD')
  }

  if (profileInfo.unit === UNIT.BLOOM && clinicalInfo?.bloom_pod_version) {
    return i18n.t(clinicalInfo.bloom_pod_version)
  }

  if (clinicalInfo?.kit) {
    return i18n.t(clinicalInfo.kit)
  }

  return ''
})

const prescriptionsRoute = (): LocationAsRelativeRaw => {
  const { clinicalInfo, profileInfo } = props

  switch (profileInfo.unit) {
    case UNIT.MOVE:
      return { name: 'profile.move-prescriptions', params: { patientID: profileInfo.user_id } }

    case UNIT.BLOOM:
    case UNIT.MIND:
      return { name: 'prescriptions', params: { memberId: profileInfo.user_id } }

    default:
      const hasSessions = clinicalInfo.total_non_default_sessions || profileInfo?.has_pending_prescription

      return { name: hasSessions ? 'profile.sessions' : 'profile.treatmentPath', params: { patientID: profileInfo.user_id } }
  }
}

const navigationMetadata = computed(() => {
  const { user_id: userId, unit } = props.profileInfo

  return [
    {
      label: 'COMMON.PRESCRIPTIONS',
      route: prescriptionsRoute(),
      icon: 'prescription',
      disabled: userIsLead.value,
      eventDestination: LeaveFeedEventDestination.PRESCRIPTION,
    },
    {
      label: 'copy_2681',
      route: { name: unit === UNIT.BLOOM ? 'therapy_results.clinical-sessions' : 'therapy_results.sessions', params: { patientID: userId } },
      icon: 'prescription-results',
      disabled: userIsLead.value,
      hidden: unit === UNIT.MIND || unit === UNIT.MOVE,
      eventDestination: LeaveFeedEventDestination.RESULTS,
    },
    {
      label: 'clinical_records',
      route: { name: 'clinical-records.listing', params: { patientID: userId } },
      icon: 'forms',
      disabled: userIsLead.value,
      eventDestination: LeaveFeedEventDestination.RECORDS,
    },
    {
      label: 'copy_2952',
      route: { name: 'profile.details', params: { patientID: userId } },
      icon: 'user',
      disabled: userIsLead.value,
      eventDestination: LeaveFeedEventDestination.PROFILE,
    },
  ].filter((item) => !item.hidden)
})

const handleWidgetLinkClick = (item: { eventDestination: LeaveFeedEventDestination }) => {
  emit('leave-feed', {
    source: FeedEventSource.SIDEBAR,
    destination: item.eventDestination,
  })
}

const copyContentToClipboard = (context: Context, content: string) => {
  console.log(context, content)

  emit('copy-to-clipboard', { context, content })
}

const startCall = (phoneNumber: string) => {
  window.open(`zoomphonecall://${phoneNumber.replace(' ', '')}`)
  emit('start-call', { source: FeedEventSource.SIDEBAR })
}
</script>

<template>
  <div class="flex size-full flex-col bg-white font-medium">
    <sword-button-wuk
      class="absolute right-1 top-1"
      theme="text"
      round
      size="small"
      icon="close"
      @click.native="$emit('close-side-bar')"
    />
    <member-drawer-content-header
      :profile="profileInfo"
      :age="age"
      :current-state="currentState"
      :eligibility="eligibility"
      @copy-to-clipboard="(context) => emit('copy-to-clipboard', context)"
      @open-ui="(context) => emit('open-ui', context)"
      @send-to-user="(context) => emit('send-to-user', context)"
    />
    <div class="overflow-auto">

      <!-- Navigation Links -->
      <section class="flex justify-center gap-4 border-b border-context-6 py-4">
        <widget-link
          v-for="item in navigationMetadata"
          :key="`navigation-${$t(item.label)}`"
          :label="item.label"
          :icon="item.icon"
          :route="item.route"
          :disabled="item.disabled"
          target="_blank"
          rel="noreferrer noopener"
          @widget-link-click="handleWidgetLinkClick(item)"
        />
      </section>

      <!-- Profile Information Sections -->
      <section v-if="profileInfo.unit !== UNIT.MIND && !!clinicalInfo?.therapy_name" class="border-b border-context-6 p-4">
        <h6 class="t3 flex items-center text-context-4">
          <span>{{ $t(clinicalInfo.therapy_name) }}</span>
          <template v-if="!!clinicalInfo.side">
            <span class="dot-separator mx-2"></span>
            <span>{{ $t(clinicalInfo.side) }}</span>
          </template>
        </h6>
        <p class="m-0">{{ $t(clinicalInfo.condition || '') }}</p>
      </section>

      <!-- Kit Information Section -->
      <section
        v-if="profileInfo.unit !== UNIT.MIND && (clinicalInfo?.kit || clinicalInfo?.kit_status)"
        class="flex items-center border-b border-context-6 p-4"
      >
        <div>
          <h6 class="t3 text-context-4">{{ $t("SIDEBAR.KIT") }}</h6>
          <p class="t2 mb-0 flex items-center">
            {{ kitText }}
            <base-pill
              v-if="clinicalInfo?.kit_status && !clinicalInfo?.no_pod"
              class="ml-2"
              :type="PILL_STATUS_TYPES.KIT"
              :status="clinicalInfo.kit_status"
            />
          </p>
        </div>
        <all-member-kits v-if="kitHistory.length > 1" :items="kitHistory" />
      </section>

      <!-- Prescription Section -->
      <section v-if="profileInfo.unit !== UNIT.MIND" class="border-b border-context-6 p-4">
        <patient-prescription
          :unit="profileInfo.unit"
          :protocol="protocol"
          :bloom-program-name="bloomProtocol?.bloom_program_name"
          :no-pod-sessions-allowed="clinicalInfo?.no_pod_sessions_allowed"
        />
        <patient-reminders :items="reminders" />
        <patient-goals
          :items="goals"
          @copy-to-clipboard="($event) => copyContentToClipboard('goals', $event)"
        />
      </section>

      <!-- Contacts Section -->
      <section class="border-b border-context-6 p-4">
        <patient-contacts
          :contacts="contacts"
          @start-call="(phoneNumber) => startCall(phoneNumber)"
          @copy-to-clipboard="({context, content}) => copyContentToClipboard(context, content)"
        />
      </section>

      <!-- Owner Information Section -->
      <section v-if="profileInfo.owner?.name" class="border-b border-context-6 p-4">
        <h6 class="t3 text-context-4">{{ $t("copy_2992") }}</h6>
        <p class="mb-0">
          {{ profileInfo.owner.name }}
          <span v-if="profileInfo.owner?.id === therapistId" class="ml-1">({{ $t("COMMON.you") }})</span>
        </p>
        <temporary-transfer-pill
          v-if="profileInfo.owner.temporary_transfer"
          class="mt-1"
          :start-date="profileInfo.owner.temporary_transfer.start_date"
          :end-date="profileInfo.owner.temporary_transfer.end_date"
        />
      </section>

      <!-- Intake PT Section -->
      <section class="border-b border-context-6 p-4">
        <h6 class="t3 text-context-4">{{ $t("INTAKE.PT") }}</h6>
        <p>
          {{ profileInfo.intake_pt?.name || "-" }}
          <span v-if="profileInfo.intake_pt?.id === therapistId">({{ $t("COMMON.you") }})</span>
        </p>
      </section>

      <!-- Care Coordinators Section -->
      <section v-if="careCoordinators?.length" class="border-b border-context-6 p-4">
        <h6 class="t3 text-context-4">{{ $t("ASSIGNED_CC_KEY") }}</h6>
        <li v-for="(cc, ccIndex) in careCoordinators" :key="ccIndex">
          {{ cc?.firstname }} {{ cc?.lastname }}
        </li>
      </section>

      <!-- App Version Section -->
      <section class="border-b border-context-6 p-4">
        <template v-if="profileInfo.unit === UNIT.SWORD">
          <h6 class="t3 text-context-4">{{ $t('SIDEBAR.DT_VERSION') }}</h6>
          <p class="mb-4">{{ clinicalInfo.apk_version || '-' }}</p>
        </template>

        <h6 class="t3 text-context-4">{{ $t('SIDEBAR.MEMBER_APP_VERSION') }}</h6>
        <p>{{ appVersion || '-' }}</p>
      </section>
    </div>
  </div>
</template>
